import React, { useState, useEffect, useMemo } from "react";
import { FaTimes, FaSearch } from "react-icons/fa";

const TICKET_TYPES = [
  "CANCELLATION",
  "RETURN",
  "REFUND",
  "DELIVERY_ISSUE",
  "SUPPLIER_RETURN",
];

const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const STATUSES = [
  "OPEN",
  "ASSIGNED",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
  "REJECTED",
];

const formatLabel = (value = "") =>
  value
    .toString()
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const Field = ({ label, children, required = false }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>
    {children}
  </div>
);

export default function TicketFormModal({
  mode = "create",
  initialData = null,
  onClose,
  onSuccess,
  apiRequest,
  backendBaseUrl,
  getHeaders,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    ticket_type: initialData?.ticket_type || "DELIVERY_ISSUE",
    description: initialData?.description || "",
    priority: initialData?.priority || "MEDIUM",
    entity_type: initialData?.entity_type || "ORDER",
    entity_id: initialData?.entity_id || "",
    owner_id:
      typeof initialData?.owner_id === "object"
        ? initialData?.owner_id?._id || ""
        : initialData?.owner_id || "",
    status: initialData?.status || "OPEN",
    remarks: initialData?.remarks || "",
  });

  // Order search states
  const [orderSearchText, setOrderSearchText] = useState(
    initialData?.entity_id || ""
  );
  const [searchedOrders, setSearchedOrders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchOrders = async (keyword) => {
    if (!keyword.trim()) {
      setSearchedOrders([]);
      return;
    }

    try {
      setIsSearching(true);
      const res = await fetch(
        `${backendBaseUrl}customer/salesorder/get-all-salesorders?status=Out for Delivery,Delivered`,
        { headers: getHeaders() }
      );
      const json = await res.json();
      const allOrders = json.data || [];

      const filtered = allOrders.filter((order) =>
        (order.quick_buy_order_number || order.salesorder_number || "")
          .toLowerCase()
          .includes(keyword.toLowerCase())
      );
      setSearchedOrders(filtered);
    } catch (err) {
      console.error(err);
      setSearchedOrders([]);
    } finally {
      setIsSearching(false);
    }
  };

  const debounceSearch = useMemo(() => {
    let timeout;
    return (value) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => searchOrders(value), 400);
    };
  }, []);

  const handleOrderInputChange = (text) => {
    setOrderSearchText(text);
    if (!text.trim()) {
      setForm((prev) => ({ ...prev, entity_id: "" }));
      setSearchedOrders([]);
      return;
    }
    if (text.trim().length >= 2) {
      debounceSearch(text);
    } else {
      setSearchedOrders([]);
    }
  };

  const handleSelectOrder = (order) => {
    const orderNumber =
      order.quick_buy_order_number || order.salesorder_number || "";
    setOrderSearchText(orderNumber);
    setForm((prev) => ({
      ...prev,
      entity_type: "ORDER",
      entity_id: order._id, // Mongo ID sent in payload
    }));
    setSearchedOrders([]);
  };

  const updateForm = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Button disabled until required fields are provided
  const isFormValid =
    form.entity_id.trim() !== "" &&
    form.ticket_type.trim() !== "" &&
    form.priority.trim() !== "" &&
    form.status.trim() !== "" &&
    form.description.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setError("");

    const payload = {
      ...form,
      description: form.description.trim(),
      entity_type: form.entity_type.trim() || "ORDER",
      entity_id: form.entity_id.trim(),
      owner_id: form.owner_id.trim() || null,
      remarks: form.remarks.trim(),
    };

    // If editing, preserve the existing ticket number in payload if backend requires it
    if (mode === "edit" && initialData?.ticket_number) {
      payload.ticket_number = initialData.ticket_number;
    }

    setSaving(true);
    try {
      if (mode === "create") {
        await apiRequest("returns/create-ticket", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        onSuccess("Ticket created successfully.");
      } else {
        const id = initialData?._id || initialData?.id;
        await apiRequest(`returns/update-ticket?id=${id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        onSuccess("Ticket updated successfully.");
      }
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save ticket.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {mode === "create" ? "Create New Ticket" : "Edit Ticket"}
            </h2>
            {mode === "edit" && initialData?.ticket_number && (
              <p className="mt-0.5 text-xs font-semibold text-gray-500">
                Ticket: <span className="text-blue-600">{initialData.ticket_number}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            
            {/* 1. ORDER SEARCH (Takes position 1 where ticket number was) */}
            <div className="relative">
              <Field label="Order Number (Entity)" required>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={orderSearchText}
                    onChange={(e) => handleOrderInputChange(e.target.value)}
                    placeholder="Search & select order number..."
                    className={`${inputClass} pl-9`}
                    required
                  />
                </div>
              </Field>

              {searchedOrders.length > 0 && (
                <div className="absolute left-0 right-0 top-[72px] z-50 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                  {searchedOrders.map((order) => {
                    const label =
                      order.quick_buy_order_number ||
                      order.salesorder_number ||
                      order._id;
                    return (
                      <button
                        key={order._id}
                        type="button"
                        onClick={() => handleSelectOrder(order)}
                        className={`w-full border-b border-gray-100 px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${
                          form.entity_id === order._id ? "bg-blue-50 font-medium" : ""
                        }`}
                      >
                        <div className="font-semibold text-gray-800">
                          {label}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.customer_id.customer_name || ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {isSearching && (
                <div className="absolute right-3 top-9 text-xs text-gray-400">
                  Searching...
                </div>
              )}
            </div>

            {/* 2. TICKET TYPE */}
            <Field label="Ticket Type" required>
              <select
                name="ticket_type"
                value={form.ticket_type}
                onChange={updateForm}
                className={inputClass}
                required
              >
                {TICKET_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {formatLabel(t)}
                  </option>
                ))}
              </select>
            </Field>

            {/* 3. PRIORITY */}
            <Field label="Priority" required>
              <select
                name="priority"
                value={form.priority}
                onChange={updateForm}
                className={inputClass}
                required
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {formatLabel(p)}
                  </option>
                ))}
              </select>
            </Field>

            {/* 4. STATUS */}
            <Field label="Status" required>
              <select
                name="status"
                value={form.status}
                onChange={updateForm}
                className={inputClass}
                required
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatLabel(s)}
                  </option>
                ))}
              </select>
            </Field>

            {/* 5. ASSIGNED OWNER */}
            <div className="sm:col-span-2">
              <Field label="Assigned Owner ID">
                <input
                  name="owner_id"
                  value={form.owner_id}
                  onChange={updateForm}
                  placeholder="User ObjectId"
                  className={inputClass}
                />
              </Field>
            </div>
          </div>

          {/* DESCRIPTION */}
          <Field label="Description" required>
            <textarea
              name="description"
              value={form.description}
              onChange={updateForm}
              rows={4}
              placeholder="Describe the issue, request, or complaint..."
              className={inputClass}
              required
            />
          </Field>

          {/* REMARKS */}
          <Field label="Remarks">
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={updateForm}
              rows={3}
              placeholder="Internal notes or action taken..."
              className={inputClass}
            />
          </Field>

          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving || !isFormValid}
              className="rounded-lg bg-[#f7525a] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#f7525a] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving
                ? "Saving..."
                : mode === "create"
                ? "Create Ticket"
                : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}